import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import Node, { InputType } from 'components_new/molecules/Node'
import AddButton from 'components_new/molecules/Node/AddButton'
import Caption from 'components_new/molecules/Node/Caption'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import FunctionSelect from 'components_new/molecules/Node/FunctionSelect'

import NodeType from 'types/transformation/enums/NodeType'
import {
  AggregateNode,
  AggregateSchema,
  NodeSchema,
  Node as TNode
} from 'types/transformation/types/nodes'

import { FUNCTIONS, getNewColumn, getOutputType } from './utils'
import { pruneRef, useInputSchema, useIsInvalid, usePruneRefs, useColumnOrigins } from '../hooks'

interface AggregateNodeProps {
  id: string
  data: AggregateNode
  selected: boolean
}

const FUNCTION_OPTIONS = FUNCTIONS.map((func) => ({
  id: func.id,
  name: func.name
}))

const Aggregate = (props: AggregateNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchema = useInputSchema(data.left_node)

  // Which source table each Input node reads, so a column can show its origin
  // table name (not just the node number). Equality keeps this stable across
  // unrelated board changes such as dragging nodes around.
  const originByNode = useColumnOrigins()

  const fieldOptions = useMemo(
    (): FieldOption[] =>
      inputSchema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [inputSchema, originByNode]
  )

  // input schema has changed, validate references
  usePruneRefs<AggregateNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      group_by: pruneRef(nodeData.group_by, inputSchema),
      schema: nodeData.schema.map((col) => {
        const leftColumn = inputSchema.find(
          (input) => input.id === col.left_column
        )

        return {
          ...col,
          left_column: leftColumn ? leftColumn.id : null,
          type: leftColumn
            ? getOutputType(col.function, leftColumn.type)
            : col.type
        }
      })
    }),
    [inputSchema]
  )

  const handleSelectGroupBy = useCallback(
    (columnId: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as AggregateNode

          newNodeData.group_by = columnId

          // remove any reference to newly selected column in schema
          newNodeData.schema = newNodeData.schema.map((col) => {
            if (col.left_column === columnId) {
              return {
                ...col,
                left_column: null
              }
            }

            return col
          })

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  const handleSelectColumn = useCallback(
    (columnIndex: number, leftColumnId: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const column = inputSchema.find(
            (col) => col.id === leftColumnId
          ) as NodeSchema

          const newNodeData = cloneDeep(node.data) as AggregateNode

          const schemaColumn = newNodeData.schema[columnIndex]

          schemaColumn.left_column = column.id
          schemaColumn.name = column.name
          schemaColumn.type = getOutputType(schemaColumn.function, column.type)

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id, inputSchema]
  )

  const handleSelectFunction = useCallback(
    (columnIndex: number, func: AggregateSchema['function']) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as AggregateNode

          const schemaColumn = newNodeData.schema[columnIndex]
          const leftColumn = inputSchema.find(
            (col) => col.id === schemaColumn.left_column
          )

          schemaColumn.function = func

          if (leftColumn) {
            schemaColumn.type = getOutputType(func, leftColumn.type)
          }

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id, inputSchema]
  )

  const handleAdd = useCallback(() => {
    setNodes((existingNodes) =>
      existingNodes.map((node) => {
        if (node.id !== id) {
          return node
        }

        const newNodeData = cloneDeep(node.data) as AggregateNode

        newNodeData.schema.push(getNewColumn(newNodeData))

        return {
          ...node,
          data: newNodeData
        }
      })
    )
  }, [setNodes, id])

  const handleRemove = useCallback(
    (columnId: string) => {
      setNodes((existingNodes) =>
        existingNodes.map((node) => {
          if (node.id !== id) {
            return node
          }

          const newNodeData = cloneDeep(node.data) as AggregateNode

          newNodeData.schema = newNodeData.schema.filter(
            (col) => col.id !== columnId
          )

          return {
            ...node,
            data: newNodeData
          }
        })
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="TuneOutlined"
      inputType={InputType.SINGLE}
      title={'Aggregate'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.AGGREGATE}
    >
      <Box sx={{ width: '360px' }}>
        <Caption>Gruppera på</Caption>
        <FieldSelect
          value={data.group_by}
          options={fieldOptions}
          onChange={handleSelectGroupBy}
        />
        <Box sx={{ mt: 1.5 }}>
          <Caption>Beräkna</Caption>
        </Box>
        {data.schema.map((column, index) => (
          <Box
            key={column.id}
            sx={{ alignItems: 'center', display: 'flex', gap: 0.75, mb: 1 }}
          >
            <FieldSelect
              value={column.left_column}
              options={fieldOptions.filter(
                (option) => option.id !== data.group_by
              )}
              onChange={(value) => handleSelectColumn(index, value)}
              placeholder="Välj attribut"
              sx={{ flex: 1, minWidth: 0 }}
            />
            <FunctionSelect
              value={column.function}
              options={FUNCTION_OPTIONS}
              onChange={(func) =>
                handleSelectFunction(index, func as AggregateSchema['function'])
              }
            />
            <IconButton onClick={() => handleRemove(column.id)}>
              <Icon name="Close" />
            </IconButton>
          </Box>
        ))}
        <AddButton onClick={handleAdd} />
      </Box>
    </Node>
  )
}

export default Aggregate
